import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Geofencing.css';
import ContentWrapper from './ContentWrapper';
import UniformTable from './UniformTable';
import { toast } from 'react-toastify';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';

const MAX_POLYGON_POINTS = 50;

const SHAPE_HELP = {
  CIRCLE: 'Click the map to set the center (drag the marker to adjust), then set the radius.',
  RECTANGLE: 'Click two opposite corners. Click a corner to start over from that corner, or drag the handles to resize.',
  POLYGON: `Click each corner in order (3 to ${MAX_POLYGON_POINTS} points). Drag a point to move it, drag a midpoint to add one, and click a point to remove it.`,
};

const DRAW_STYLE = {
  strokeColor: '#0000FF',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: '#0000FF',
  fillOpacity: 0.35,
};

const SAVED_STYLE = {
  strokeColor: '#FF0000',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: '#FF0000',
  fillOpacity: 0.35,
};

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

const formatArea = (sq) => {
  const n = Number(sq);
  if (!Number.isFinite(n)) return '';
  return n >= 1e6 ? `${(n / 1e6).toFixed(2)} km²` : `${Math.round(n)} m²`;
};

const boundsToCorners = (b) => {
  const sw = b.getSouthWest();
  const ne = b.getNorthEast();
  return {
    sw: { lat: sw.lat(), lng: sw.lng() },
    ne: { lat: ne.lat(), lng: ne.lng() },
  };
};

export default function Geofencing({ devices, user, onFullWidthChange, setBackAction, setPageTitle }) {
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    if (onFullWidthChange) onFullWidthChange(Boolean(selectedDevice));
  }, [selectedDevice, onFullWidthChange]);

  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^\/geofencing\/(.+)$/);
      if (match) {
        const found = devices.find(d => d.imei === match[1]);
        setSelectedDevice(found || null);
      } else {
        setSelectedDevice(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [devices]);

  useEffect(() => {
    if (selectedDevice && setBackAction) {
      setBackAction(() => () => window.history.back());
    }
    return () => { if (setBackAction) setBackAction(null); };
  }, [selectedDevice, setBackAction]);

  useEffect(() => {
    if (selectedDevice && setPageTitle) {
      setPageTitle(`Geofencing: RF ID (${selectedDevice.imei})`);
    } else if (setPageTitle) {
      setPageTitle('');
    }
    return () => { if (setPageTitle) setPageTitle(''); };
  }, [selectedDevice, setPageTitle]);

  if (selectedDevice) {
    return (
      <GeofenceManager
        device={selectedDevice}
        user={user}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <ContentWrapper title="Geofencing">
      <DeviceList
        devices={devices}
        onSelect={(d) => {
          setSelectedDevice(d);
          window.history.pushState({}, '', `/geofencing/${d.imei}`);
        }}
      />
    </ContentWrapper>
  );
}

function DeviceList({ devices, onSelect }) {
  const [search, setSearch] = useState('');

  const filtered = devices.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.imei || '').includes(search)
  );

  const columns = [
    { header: '#', accessor: 'number' },
    { header: 'Name', accessor: 'name' },
    { header: 'IMEI', accessor: 'imei' },
    { header: 'Action', accessor: 'action' }
  ];

  const rows = filtered.map((d, i) => ({
    number: (
      <span className="badge fleet-table-index">
        {String(i + 1).padStart(2, '0')}
      </span>
    ),
    name: d.name || d.deviceName,
    imei: <span className="mono">{d.imei}</span>,
    action: (
      <div style={{ textAlign: 'center' }}>
        <button className="soft" onClick={() => onSelect(d)}>
          Select
        </button>
      </div>
    )
  }));

  return (
    <div className="trip-report devices">
      {/* Desktop & Tablet: Table Layout */}
      <div className="devices-desktop">
        <div className="devices-header-row">
          <input
            className="devices-search-input"
            type="text"
            placeholder="Search name, IMEI…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="devices-table-container fleet-table-wrapper">
          {rows.length === 0 ? (
            <div className="align-center fleet-table-empty">No devices found</div>
          ) : (
            <UniformTable columns={columns} data={rows} />
          )}
        </div>
      </div>

      {/* Mobile: Card Layout */}
      <div className="devices-mobile">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        </div>
        <input className="mobile-search" type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="mobile-devices-container">
          {filtered.length === 0 ? (
            <div className="mobile-empty">No devices found</div>
          ) : (
            filtered.map((d, i) => (
              <div key={d.imei} className="mobile-device-card">
                <div className="device-card-header">
                  <h3 className="device-name">{d.name || d.deviceName}</h3>
                </div>
                <div className="device-card-body">
                  <div className="device-info">
                    <span className="info-label">IMEI:</span>
                    <span className="info-value mono">{d.imei}</span>
                  </div>
                </div>
                <div className="device-card-actions">
                  <button
                    className="mobile-action-btn primary"
                    onClick={() => onSelect(d)}
                  >
                    Select
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function GeofenceManager({ device, user, onBack }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const savedShapesRef = useRef([]);   // saved geofences drawn on the map (red)
  const geofencesRef = useRef([]);     // latest saved list (so the map can redraw after it initialises)
  const hasFitGeofenceBoundsRef = useRef(false);
  const hasUserInteractedWithMapRef = useRef(false);

  // Everything the user is currently drawing (blue)
  const previewRef = useRef({ marker: null, circle: null, rect: null, polygon: null, corner: null });
  const rectCornerRef = useRef(null);  // first rectangle corner, before the second click

  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useFleetGoogleMaps();

  // Form State
  const [shapeType, setShapeType] = useState('CIRCLE'); // CIRCLE | RECTANGLE | POLYGON
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState(1000);
  const [eventType, setEventType] = useState('BOTH');
  const [creating, setCreating] = useState(false);
  const [rectBounds, setRectBounds] = useState(null); // { sw:{lat,lng}, ne:{lat,lng} }
  const [polyCount, setPolyCount] = useState(0);

  // Data State
  const [geofences, setGeofences] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Refs read by the map click listener (the listener is created once)
  const shapeTypeRef = useRef(shapeType);
  useEffect(() => { shapeTypeRef.current = shapeType; }, [shapeType]);
  const handleMapClickRef = useRef(() => { });

  /* ---------------- saved geofences (red) ---------------- */

  const drawGeofences = useCallback((list) => {
    if (!mapInstance.current || !window.google) return;
    const g = window.google;

    savedShapesRef.current.forEach(s => s.setMap(null));
    savedShapesRef.current = [];

    const bounds = new g.maps.LatLngBounds();

    list.forEach(gf => {
      const shape = gf.shape_type || 'CIRCLE';
      const hasPolygon = shape !== 'CIRCLE' && Array.isArray(gf.coordinates) && gf.coordinates.length >= 3;
      const center = { lat: parseFloat(gf.center_lat), lng: parseFloat(gf.center_lng) };

      let overlay;
      if (hasPolygon) {
        // Draw the REAL shape. Do not draw the bounding circle (center/radius) for these.
        const path = gf.coordinates.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }));
        overlay = new g.maps.Polygon({ ...SAVED_STYLE, map: mapInstance.current, paths: path });
        path.forEach(p => bounds.extend(p));
      } else {
        overlay = new g.maps.Circle({
          ...SAVED_STYLE,
          map: mapInstance.current,
          center,
          radius: parseFloat(gf.radius_meters),
        });
        const cb = overlay.getBounds();
        if (cb) bounds.union(cb);
      }
      savedShapesRef.current.push(overlay);

      overlay.addListener('click', (e) => {
        const p = previewRef.current;
        const drawing = Boolean(
          p.marker || p.rect || p.corner || (p.polygon && p.polygon.getPath().getLength() > 0)
        );
        // While the user is drawing, a click on top of a saved fence must still add a point.
        if (drawing && e && e.latLng) {
          handleMapClickRef.current(e.latLng.lat(), e.latLng.lng());
          return;
        }
        const detail = hasPolygon
          ? `${escapeHtml(shape)} · ${escapeHtml(formatArea(gf.area_sq_m))}`
          : `Radius: ${escapeHtml(gf.radius_meters)}m`;
        new g.maps.InfoWindow({
          content: `<div><b>${escapeHtml(gf.name)}</b><br/>${detail}</div>`,
          position: (e && e.latLng) || center,
        }).open(mapInstance.current);
      });
    });

    if (
      list.length > 0 &&
      !bounds.isEmpty() &&
      !hasFitGeofenceBoundsRef.current &&
      !hasUserInteractedWithMapRef.current
    ) {
      hasFitGeofenceBoundsRef.current = true;
      mapInstance.current.fitBounds(bounds);
    }
  }, []);

  const fetchLatestLocation = useCallback(async () => {
    try {
      // Build 7 day range to ensure we get last point
      const now = new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const sy = start.getFullYear();
      const sm = String(start.getMonth() + 1).padStart(2, '0');
      const sd = String(start.getDate()).padStart(2, '0');
      const ey = now.getFullYear();
      const em = String(now.getMonth() + 1).padStart(2, '0');
      const ed = String(now.getDate()).padStart(2, '0');
      const range = `${sy}${sm}${sd}000000,${ey}${em}${ed}235959`;
      const url = `https://mo4v7gedrd.execute-api.ap-southeast-1.amazonaws.com/testing3/testing3?imeis=${encodeURIComponent(device.imei)}&TimeRange=${range}`;
      const res = await fetch(url);
      const json = await res.json();
      const arr = Array.isArray(json?.data) ? json.data : [];

      // Get latest point
      const last = arr.reduce((a, b) => (a && a.time_stamp > b.time_stamp ? a : b), null) || arr[arr.length - 1];

      if (last && last.latitude && last.longitude) {
        const pos = { lat: parseFloat(last.latitude), lng: parseFloat(last.longitude) };
        if (!isNaN(pos.lat) && !isNaN(pos.lng) && mapInstance.current) {
          // Center map
          mapInstance.current.setCenter(pos);
          mapInstance.current.setZoom(14);

          // Add "Current Location" marker
          new window.google.maps.Marker({
            position: pos,
            map: mapInstance.current,
            title: 'Current Device Location',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch latest location', err);
    }
  }, [device.imei]);

  const fetchGeofences = useCallback(async () => {
    setLoadingList(true);
    try {
      const url = `https://ix2x4h6ang.execute-api.ap-southeast-1.amazonaws.com/prod/manage-geofences?imei=${device.imei}&action=list`;
      const res = await fetch(url);
      const json = await res.json();
      const list = Array.isArray(json.geofences) ? json.geofences : [];
      geofencesRef.current = list;
      setGeofences(list);
      drawGeofences(list);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load existing geofences');
    } finally {
      setLoadingList(false);
    }
  }, [device.imei, drawGeofences]);

  /* ---------------- new shape being drawn (blue) ---------------- */

  const clearPreviewOverlays = useCallback(() => {
    const p = previewRef.current;
    ['marker', 'circle', 'rect', 'polygon', 'corner'].forEach(k => {
      if (p[k]) {
        p[k].setMap(null);
        p[k] = null;
      }
    });
    rectCornerRef.current = null;
  }, []);

  const resetDrawing = useCallback(() => {
    clearPreviewOverlays();
    setLat('');
    setLng('');
    setRectBounds(null);
    setPolyCount(0);
  }, [clearPreviewOverlays]);

  // Circle preview (marker + circle)
  const updatePreview = useCallback((l, ln, r) => {
    if (!mapInstance.current || !window.google) return;
    if (shapeTypeRef.current !== 'CIRCLE') return;

    const latNum = parseFloat(l);
    const lngNum = parseFloat(ln);
    let rNum = parseFloat(r);
    if (!Number.isFinite(rNum) || rNum <= 0) rNum = 100;

    if (isNaN(latNum) || isNaN(lngNum)) {
      // Remove preview if inputs are invalid to avoid internal map errors
      if (previewRef.current.circle) {
        previewRef.current.circle.setMap(null);
        previewRef.current.circle = null;
      }
      if (previewRef.current.marker) {
        previewRef.current.marker.setMap(null);
        previewRef.current.marker = null;
      }
      return;
    }

    const pos = { lat: latNum, lng: lngNum };

    // Marker
    if (!previewRef.current.marker) {
      previewRef.current.marker = new window.google.maps.Marker({
        position: pos,
        map: mapInstance.current,
        draggable: true,
        title: 'New Geofence Center',
      });
      previewRef.current.marker.addListener('dragend', (e) => {
        setLat(e.latLng.lat().toFixed(6));
        setLng(e.latLng.lng().toFixed(6));
      });
    } else {
      previewRef.current.marker.setPosition(pos);
    }

    // Circle
    if (!previewRef.current.circle) {
      previewRef.current.circle = new window.google.maps.Circle({
        ...DRAW_STYLE,
        map: mapInstance.current,
        center: pos,
        radius: rNum,
      });
    } else {
      previewRef.current.circle.setCenter(pos);
      previewRef.current.circle.setRadius(rNum);
    }
  }, []);

  // Polygon: editable, so the user can drag points, drag midpoints to add, right-click to delete
  const createPolygon = () => {
    const g = window.google;
    const path = new g.maps.MVCArray();
    const poly = new g.maps.Polygon({
      ...DRAW_STYLE,
      map: mapInstance.current,
      editable: true,
    });
    // setPath() attaches OUR MVCArray as the polygon's first path, so poly.getPath()
    // returns it and editing (drag / add / delete points) updates it.
    // (Passing an empty MVCArray as `paths` is read as "an empty list of paths",
    // which made getPath() return undefined.)
    poly.setPath(path);
    const sync = () => setPolyCount(path.getLength());
    ['insert_at', 'remove_at', 'set_at'].forEach(ev => path.addListener(ev, sync));

    poly.addListener('contextmenu', (e) => {
      if (e.vertex !== undefined) path.removeAt(e.vertex);
    });
    // Clicking a vertex removes it; clicking inside the polygon adds another point.
    poly.addListener('click', (e) => {
      if (e.vertex !== undefined) {
        path.removeAt(e.vertex);
        return;
      }
      if (e.vertex === undefined && e.edge === undefined) {
        handleMapClickRef.current(e.latLng.lat(), e.latLng.lng());
      }
    });
    return poly;
  };

  // Updated on every render so it always sees the latest state
  handleMapClickRef.current = (la, ln) => {
    const map = mapInstance.current;
    const g = window.google;
    if (!map || !g) return;
    const type = shapeTypeRef.current;
    const p = previewRef.current;

    if (type === 'CIRCLE') {
      setLat(la.toFixed(6));
      setLng(ln.toFixed(6));
      return;
    }

    if (type === 'RECTANGLE') {
      if (p.rect) return; // already drawn: user adjusts it with the handles

      if (!rectCornerRef.current) {
        rectCornerRef.current = { lat: la, lng: ln };
        p.corner = new g.maps.Marker({ position: { lat: la, lng: ln }, map, title: 'First corner' });
        return;
      }

      const a = rectCornerRef.current;
      const bounds = {
        south: Math.min(a.lat, la),
        north: Math.max(a.lat, la),
        west: Math.min(a.lng, ln),
        east: Math.max(a.lng, ln),
      };
      if (p.corner) {
        p.corner.setMap(null);
        p.corner = null;
      }
      rectCornerRef.current = null;

      const rect = new g.maps.Rectangle({
        ...DRAW_STYLE,
        map,
        bounds,
        editable: true,
        draggable: true,
      });
      rect.addListener('bounds_changed', () => setRectBounds(boundsToCorners(rect.getBounds())));
      rect.addListener('click', (e) => {
        if (!e.latLng) return;

        const currentBounds = rect.getBounds();
        if (!currentBounds) return;

        const sw = currentBounds.getSouthWest();
        const ne = currentBounds.getNorthEast();
        const corners = [
          { lat: sw.lat(), lng: sw.lng() },
          { lat: sw.lat(), lng: ne.lng() },
          { lat: ne.lat(), lng: sw.lng() },
          { lat: ne.lat(), lng: ne.lng() },
        ];
        const clickLat = e.latLng.lat();
        const clickLng = e.latLng.lng();
        const nearestCorner = corners.reduce((nearest, corner) => {
          const latDistance = (corner.lat - clickLat) * 111320;
          const lngDistance = (corner.lng - clickLng) * 111320 * Math.cos(clickLat * Math.PI / 180);
          const distance = Math.hypot(latDistance, lngDistance);
          return distance < nearest.distance ? { corner, distance } : nearest;
        }, { corner: null, distance: Infinity });
        const metersPerPixel = 156543.03392 * Math.cos(clickLat * Math.PI / 180) / (2 ** (map.getZoom() || 0));

        if (nearestCorner.distance > metersPerPixel * 20) return;

        rect.setMap(null);
        p.rect = null;
        rectCornerRef.current = nearestCorner.corner;
        p.corner = new g.maps.Marker({
          position: nearestCorner.corner,
          map,
          title: 'First corner',
        });
        setRectBounds(null);
      });
      p.rect = rect;
      setRectBounds(boundsToCorners(rect.getBounds()));
      return;
    }

    // POLYGON
    if (!p.polygon) p.polygon = createPolygon();
    const path = p.polygon.getPath();
    if (path.getLength() >= MAX_POLYGON_POINTS) {
      toast.warn(`A polygon can have at most ${MAX_POLYGON_POINTS} points`);
      return;
    }
    path.push(new g.maps.LatLng(la, ln));
  };

  /* ---------------- map + data loading ---------------- */

  // Initialize Map (only when Google Maps finishes loading - NOT on every radius change)
  useEffect(() => {
    let cancelled = false;

    const initializeMap = async () => {
      try {
        if (!mapsLoaded) {
          if (mapsLoadError) {
            toast.error('Failed to load Google Maps');
          }
          return;
        }

        if (cancelled || !mapRef.current) return;

        await new Promise((resolve) => requestAnimationFrame(() => resolve()));
        if (cancelled || !mapRef.current) return;

        if (mapInstance.current) {
          drawGeofences(geofencesRef.current);
          return;
        }

        const g = window.google;
        let MapConstructor = g?.maps?.Map;
        try {
          if (g?.maps?.importLibrary) {
            const mapsLib = await g.maps.importLibrary('maps');
            if (mapsLib && mapsLib.Map) MapConstructor = mapsLib.Map;
            await g.maps.importLibrary('marker').catch(() => { });
          }
        } catch (e) {
          console.warn('Maps importLibrary failed', e);
        }

        if (!MapConstructor) {
          console.error('Map constructor not found');
          return;
        }

        const map = new MapConstructor(mapRef.current, {
          center: { lat: 20.5937, lng: 78.9629 },
          zoom: 5,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          attributionControl: false,
        });
        mapInstance.current = map;

        if (g.maps.event && typeof g.maps.event.trigger === 'function') {
          g.maps.event.trigger(map, 'resize');
        }

        map.addListener('click', (e) => {
          hasUserInteractedWithMapRef.current = true;
          handleMapClickRef.current(e.latLng.lat(), e.latLng.lng());
        });
        map.addListener('dragstart', () => {
          hasUserInteractedWithMapRef.current = true;
        });
        map.addListener('zoom_changed', () => {
          hasUserInteractedWithMapRef.current = true;
        });

        // Saved geofences may have loaded before the map existed
        drawGeofences(geofencesRef.current);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error('Failed to load Google Maps');
        }
      }
    };

    initializeMap();
    return () => {
      cancelled = true;
    };
  }, [mapsLoaded, mapsLoadError, drawGeofences]);

  // Fetch Geofences
  useEffect(() => {
    fetchGeofences();
    fetchLatestLocation();
  }, [device.imei, fetchGeofences, fetchLatestLocation]);

  // Switching shape type clears whatever was being drawn
  useEffect(() => {
    resetDrawing();
  }, [shapeType, resetDrawing]);

  // Update circle preview when inputs change
  useEffect(() => {
    updatePreview(lat, lng, radius);
  }, [lat, lng, radius, updatePreview]);

  /* ---------------- create / delete ---------------- */

  const buildPayload = () => {
    const base = {
      imei: device.imei,
      name: name.trim(),
      user_id: user, // From prop (email usually)
      event_type: eventType,
      shape_type: shapeType,
    };

    if (!base.name) return { error: 'Please enter a name' };

    if (shapeType === 'CIRCLE') {
      if (!lat || !lng || !radius) return { error: 'Click the map to set the center, and enter a radius' };
      return {
        payload: {
          ...base,
          center_lat: parseFloat(lat),
          center_lng: parseFloat(lng),
          radius_meters: parseFloat(radius),
        },
      };
    }

    if (shapeType === 'RECTANGLE') {
      if (!rectBounds) return { error: 'Click two opposite corners on the map to draw the rectangle' };
      // The backend calculates center and radius itself
      return { payload: { ...base, sw: rectBounds.sw, ne: rectBounds.ne } };
    }

    // POLYGON - points in the order they were clicked
    const poly = previewRef.current.polygon;
    const points = poly
      ? poly.getPath().getArray().map(ll => ({ lat: ll.lat(), lng: ll.lng() }))
      : [];
    if (points.length < 3) return { error: 'A polygon needs at least 3 points' };
    if (points.length > MAX_POLYGON_POINTS) return { error: `A polygon can have at most ${MAX_POLYGON_POINTS} points` };
    return { payload: { ...base, points } };
  };

  const handleCreate = async () => {
    const { payload, error } = buildPayload();
    if (error) {
      toast.warn(error);
      return;
    }

    setCreating(true);
    try {
      const apiKey = process.env.REACT_APP_API_KEY || '';
      const res = await fetch(`${process.env.REACT_APP_GEOFENCE_API_BASE}/create-geofence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'x-api-key': apiKey } : {}) },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (res.ok) {
        toast.success('Geofence created successfully');
        setName('');
        resetDrawing();
        fetchGeofences(); // Refresh list
      } else {
        // The backend returns { error: "..." }
        toast.error('Failed to create geofence: ' + (json.error || json.message || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error creating geofence');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this geofence?')) return;
    try {
      const url = `https://ix2x4h6ang.execute-api.ap-southeast-1.amazonaws.com/prod/manage-geofences?imei=${device.imei}&geofence_id=${id}&action=delete`;

      const res = await fetch(url);
      //       const url = `${process.env.REACT_APP_GEOFENCE_API_BASE}/manage-geofences?imei=${device.imei}&geofence_id=${id}&action=delete`;
      // const res = await fetch(url, { headers: apiKey ? { 'x-api-key': apiKey } : undefined });
      const json = await res.json();
      if (res.ok) {
        toast.success('Geofence deleted');
        fetchGeofences();
      } else {
        toast.error('Failed to delete: ' + (json.error || json.message || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error deleting geofence');
    }
  };

  const fmt = (n) => (Number.isFinite(n) ? n.toFixed(6) : '');

  return (
    <div className="geofencing-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Removed duplicate back button here since it's in the top bar */}
      </div>
      <div className="geo-manager">
        {/* Left Sidebar: Form and List */}
        <div className="geo-sidebar">
          {/* Create Form */}
          <div className="geo-form-card">
            <h3>Create Geofence</h3>

            <div className="geo-form-group">
              <label>Name</label>
              <input
                className="geo-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Home, Office"
              />
            </div>

            <div className="geo-form-group">
              <label>Shape</label>
              <select
                className="geo-select"
                value={shapeType}
                onChange={e => setShapeType(e.target.value)}
              >
                <option value="CIRCLE">Circle</option>
                <option value="RECTANGLE">Rectangle</option>
                <option value="POLYGON">Polygon</option>
              </select>
              <p style={{ fontSize: 12, opacity: 0.75, margin: '6px 0 0' }}>
                {SHAPE_HELP[shapeType]}
              </p>
            </div>

            {/* ---- Circle fields ---- */}
            {shapeType === 'CIRCLE' && (
              <>
                <div className="geo-row">
                  <div className="geo-form-group">
                    <label>Lat</label>
                    <input
                      className="geo-input"
                      value={lat}
                      onChange={e => setLat(e.target.value)}
                      placeholder="Click on map"
                    />
                  </div>
                  <div className="geo-form-group">
                    <label>Lng</label>
                    <input
                      className="geo-input"
                      value={lng}
                      onChange={e => setLng(e.target.value)}
                      placeholder="Click on map"
                    />
                  </div>
                </div>
                <div className="geo-row">
                  <div className="geo-form-group">
                    <label>Radius (m)</label>
                    <input
                      type="number"
                      className="geo-input"
                      min="1"
                      step="10"
                      value={radius}
                      onChange={e => setRadius(e.target.value)}
                    />
                  </div>
                  <div className="geo-form-group">
                    <label>Event Type</label>
                    <select
                      className="geo-select"
                      value={eventType}
                      onChange={e => setEventType(e.target.value)}
                    >
                      <option value="BOTH">BOTH</option>
                      <option value="ENTER">ENTER</option>
                      <option value="EXIT">EXIT</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* ---- Rectangle fields (read-only, set by drawing) ---- */}
            {shapeType === 'RECTANGLE' && (
              <>
                <div className="geo-row">
                  <div className="geo-form-group">
                    <label>SW Lat</label>
                    <input className="geo-input" readOnly value={fmt(rectBounds?.sw.lat)} placeholder="Draw on map" />
                  </div>
                  <div className="geo-form-group">
                    <label>SW Lng</label>
                    <input className="geo-input" readOnly value={fmt(rectBounds?.sw.lng)} placeholder="Draw on map" />
                  </div>
                </div>
                <div className="geo-row">
                  <div className="geo-form-group">
                    <label>NE Lat</label>
                    <input className="geo-input" readOnly value={fmt(rectBounds?.ne.lat)} placeholder="Draw on map" />
                  </div>
                  <div className="geo-form-group">
                    <label>NE Lng</label>
                    <input className="geo-input" readOnly value={fmt(rectBounds?.ne.lng)} placeholder="Draw on map" />
                  </div>
                </div>
              </>
            )}

            {/* ---- Polygon fields ---- */}
            {shapeType === 'POLYGON' && (
              <div className="geo-form-group">
                <label>Points</label>
                <input
                  className="geo-input"
                  readOnly
                  value={`${polyCount} / ${MAX_POLYGON_POINTS}${polyCount > 0 && polyCount < 3 ? ' (need at least 3)' : ''}`}
                />
              </div>
            )}

            {/* Event type for rectangle / polygon (circle has it in its own row) */}
            {shapeType !== 'CIRCLE' && (
              <div className="geo-form-group">
                <label>Event Type</label>
                <select
                  className="geo-select"
                  value={eventType}
                  onChange={e => setEventType(e.target.value)}
                >
                  <option value="BOTH">BOTH</option>
                  <option value="ENTER">ENTER</option>
                  <option value="EXIT">EXIT</option>
                </select>
              </div>
            )}

            <button
              type="button"
              className="soft"
              style={{ marginBottom: 8 }}
              onClick={resetDrawing}
              disabled={creating}
            >
              Clear drawing
            </button>

            <button
              className="geo-submit-btn"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Geofence'}
            </button>
          </div>

          {/* List */}
          <div className="geo-list-card">
            <h3>Active Geofences</h3>
            {loadingList ? (
              <p>Loading...</p>
            ) : geofences.length === 0 ? (
              <p>No active geofences.</p>
            ) : (
              <div>
                {geofences.map(gf => {
                  const shape = gf.shape_type || 'CIRCLE';
                  const evt = gf.event_type || 'BOTH';
                  return (
                    <div key={gf.geofence_id} className="geo-list-item">
                      <div className="geo-item-info">
                        <h4>{gf.name}</h4>
                        <p>
                          <span className={`geo-tag ${evt.toLowerCase()}`}>{evt}</span>
                          <span>
                            {' • '}
                            {shape === 'CIRCLE'
                              ? `Circle · ${gf.radius_meters}m`
                              : `${shape === 'RECTANGLE' ? 'Rectangle' : 'Polygon'} · ${formatArea(gf.area_sq_m)}`}
                          </span>
                        </p>
                      </div>
                      <button
                        className="geo-delete-btn"
                        onClick={() => handleDelete(gf.geofence_id)}
                      >
                        Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Map */}
        <div className="geo-map-container">
          <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: '600px' }} />
        </div>
      </div>
    </div>
  );
}